import { useState } from 'react';
import { K } from '../kabinet/atoms';
import { IconExport } from '../ui/icons';
import { Выпадение } from '../ui/dropdown';
import { адресВыгрузки, ВЫГРУЗКИ, ПРИПИСКА_ВЫГРУЗКИ, ФОРМАТЫ, type Формат } from './vygruzki';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ВЫГРУЗКА — ЗНАЧОК РЯДОМ С ПЕРИОДОМ И МЕНЮ ПОД НИМ.
 *
 * До 26.08 здесь стояла широкая кнопка «Выгрузить в Excel». Она делала
 * ровно одно дело и при этом отжимала кнопку периода: на сверке период
 * выходил 371 пиксель против эталонных 235. В эталоне на её месте
 * значок 34×34, а под ним меню: пять выгрузок с пояснениями
 * и переключатель XLSX/CSV.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ССЫЛКИ, А НЕ ЗАПРОСЫ В КОДЕ
 *
 * Ключ доступа лежит в печенье того же источника: браузер отправит его
 * сам и сам же сохранит файл. Через `fetch` пришлось бы собирать Blob
 * и создавать ссылку руками — больше кода ради того же самого.
 *
 * ПЕРИОД И ОТБОР ПРИХОДЯТ СТРОКОЙ С ЭКРАНА и не собираются здесь
 * заново. Собери их второй раз — и однажды в файле окажется не тот
 * период, что в таблице. Спорить с таким файлом невозможно: он
 * выглядит как правда.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ДВЕ СТРОКИ ПОМЕЧЕНЫ «В РАЗРАБОТКЕ» — И ОСТАЮТСЯ В СПИСКЕ
 *
 * «Поиск и рекомендации» и «Другие заказы» требуют данных, которых
 * служба пока не собирает. Убрать их значило бы разойтись с дизайном,
 * а отдать по ним файл с заголовками и без строк — соврать: пустой
 * файл читается как «ничего не было».
 *
 * Метка та же, что у раздела «Финансовая аналитика» в меню разделов:
 * в дизайне это уже заведённый способ сказать «есть, но позже».
 * ═══════════════════════════════════════════════════════════════════════
 */
export function Выгрузка({ base, q }: { base: string; q: string }) {
  const [открыто, setОткрыто] = useState(false);
  const [формат, setФормат] = useState<Формат>('xlsx');

  const закрыть = () => setОткрыто(false);

  return (
    <span className={K.уВыгрузки}>
      <button
        type="button"
        data-d="кнопка-выгрузки"
        title="Выгрузить в Excel"
        aria-label="Выгрузить в Excel"
        aria-haspopup="menu"
        aria-expanded={открыто}
        className={K.кнопкаВыгрузки}
        onClick={() => setОткрыто((v) => !v)}
      >
        <IconExport size={15} />
      </button>

      <Выпадение открыто={открыто} закрыть={закрыть}>
        <span role="menu" data-d="меню-выгрузки" className={K.менюВыгрузки}>
          <span className={K.шапкаМенюВыгрузки}>
            <b className={K.названиеМенюВыгрузки}>Что выгрузить</b>

            {/* ФОРМАТ ВЫБИРАЕТСЯ ДО ВЫГРУЗКИ, А НЕ ПОСЛЕ. Переключатель
                стоит в шапке меню, потому что он относится ко всем пяти
                строкам сразу: пять пар «строка × формат» были бы десятью
                строками и вдвое более длинным меню. */}
            <span data-d="формат-выгрузки" className={K.форматВыгрузки}>
              {ФОРМАТЫ.map((ф) => (
                <button
                  key={ф}
                  type="button"
                  aria-pressed={ф === формат}
                  className={ф === формат ? K.кнопкаФорматаВыбрана : K.кнопкаФормата}
                  onClick={() => setФормат(ф)}
                >
                  {ф.toUpperCase()}
                </button>
              ))}
            </span>
          </span>

          {ВЫГРУЗКИ.map((в) =>
            в.готово ? (
              <a
                key={в.ключ}
                role="menuitem"
                data-d="вариант-выгрузки"
                className={`${K.вариантВыгрузки} no-underline`}
                href={адресВыгрузки(base, в.ключ, формат, q)}
                download
                onClick={закрыть}
              >
                <span className={K.названиеВарианта}>{в.название}</span>
                <span className={K.подписьВарианта}>{в.пояснение}</span>
              </a>
            ) : (
              /* НЕ ССЫЛКА И НЕ КНОПКА: нажимать нечего, и вид обязан
                 сказать это раньше пальца — ровно как у раздела
                 «Финансовая аналитика» в меню разделов. */
              <span
                key={в.ключ}
                role="menuitem"
                aria-disabled="true"
                data-d="вариант-выгрузки"
                className={K.вариантВыгрузки}
                title="Служба пока не собирает эти данные"
              >
                <span className={K.рядПункта}>
                  <span className={K.названиеВарианта}>{в.название}</span>
                  <span data-d="метка-скоро" className={K.меткаСкоро}>
                    в разработке
                  </span>
                </span>
                <span className={K.подписьВарианта}>{в.пояснение}</span>
              </span>
            ),
          )}

          <span data-d="подпись-выгрузки" className={K.подписьВыгрузки}>
            {ПРИПИСКА_ВЫГРУЗКИ}
          </span>
        </span>
      </Выпадение>
    </span>
  );
}
